import { useState } from "react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { MachineTable } from "@/components/machine-table";
import { OwnerAvatar } from "@/components/owner-avatar";
import { ProviderLogo } from "@/components/provider-logo";
import { Button } from "@/components/ui/button";
import { UserResourcesDialog } from "@/components/user-resources-dialog";
import { useAuth } from "@/contexts/auth-context";
import {
  useAcquireK8sCluster,
  useK8sClusterMachines,
  useK8sClusters,
  useMarkK8sClusterProduction,
  useReleaseK8sCluster,
  useUnmarkK8sClusterProduction,
} from "@/hooks/use-k8s-clusters";
import { usePageTitle } from "@/hooks/use-page-title";
import type { K8sCluster } from "@/lib/types";
import { cn } from "@/lib/utils";

function ClusterStatusBadge({ status }: { status: string }) {
  const isRunning = status.toUpperCase() === "RUNNING";
  const colorClass = isRunning
    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
    : "bg-muted text-muted-foreground";

  return (
    <span className={cn("inline-block rounded-md px-2 py-0.5 text-[11px] font-medium", colorClass)}>
      {status}
    </span>
  );
}

function ProductionBadge() {
  return (
    <span className="inline-block rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
      Production
    </span>
  );
}

function NodeGroupPills({ cluster }: { cluster: K8sCluster }) {
  if (cluster.node_groups.length === 0) {
    return <span className="text-[11px] text-muted-foreground">No node groups</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {cluster.node_groups.map((nodeGroup) => (
        <span
          key={nodeGroup.id}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-0.5 text-[11px] font-mono"
        >
          <span className="text-foreground/80">
            {nodeGroup.node_count}&times; {nodeGroup.gpu_count}
            {nodeGroup.gpu_type.toUpperCase()}
          </span>
          <span className="text-muted-foreground">{nodeGroup.name}</span>
        </span>
      ))}
    </div>
  );
}

function ClusterMachinesSection({ clusterId }: { clusterId: number }) {
  const { user } = useAuth();
  const { data: machines = [], isLoading } = useK8sClusterMachines(clusterId);

  if (isLoading) {
    return (
      <div className="py-4 text-center text-[12px] text-muted-foreground">Loading instances...</div>
    );
  }

  if (machines.length === 0) {
    return (
      <div className="py-4 text-center text-[12px] text-muted-foreground">
        No instances linked to this cluster
      </div>
    );
  }

  return (
    <MachineTable
      machines={machines}
      currentUserId={user?.id ?? 0}
      isAdmin={user?.is_admin}
      stickyHeaderClassName="bg-card"
    />
  );
}

function ClusterCard({ cluster }: { cluster: K8sCluster }) {
  const [expanded, setExpanded] = useState(false);
  const [dialog, setDialog] = useState<"mark" | "unmark" | null>(null);
  const { user } = useAuth();
  const acquireCluster = useAcquireK8sCluster();
  const releaseCluster = useReleaseK8sCluster();
  const markProduction = useMarkK8sClusterProduction();
  const unmarkProduction = useUnmarkK8sClusterProduction();

  const isAdmin = Boolean(user?.is_admin);
  const isProduction = cluster.production;
  const isFree = cluster.allocated_to === null;
  const isOwnedByMe = cluster.allocated_to === user?.id;
  const canRelease = !isProduction && (isOwnedByMe || (isAdmin && cluster.allocated_to !== null));

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full flex-col gap-3 rounded-t-xl px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5 sm:py-4 text-left transition-colors hover:bg-accent"
      >
        <div className="flex items-center gap-3">
          <ProviderLogo provider="nebius" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[14px] font-semibold text-foreground">
                {cluster.name}
              </span>
              <span className="text-[13px] text-muted-foreground">&middot; {cluster.region}</span>
              <ClusterStatusBadge status={cluster.status} />
              {isProduction && <ProductionBadge />}
            </div>
            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
              {cluster.nebius_id}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* GPU / Node summary */}
          <div className="text-right">
            <div className="font-mono text-[14px] font-semibold text-foreground/80">
              {cluster.total_gpus} GPUs &middot; {cluster.total_nodes} nodes
            </div>
            <div className="text-[11px] text-muted-foreground">
              {cluster.node_groups.length} node group
              {cluster.node_groups.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Owner (pool clusters only — production clusters aren't pool-owned) */}
          {!isProduction && (
            <div className="text-right min-w-[60px]">
              {cluster.owner_name && cluster.allocated_to ? (
                <UserResourcesDialog
                  ownerName={cluster.owner_name}
                  ownerId={cluster.allocated_to}
                  ownerAvatarUrl={cluster.owner_avatar_url}
                >
                  <div className="flex items-center gap-1.5">
                    <OwnerAvatar name={cluster.owner_name} avatarUrl={cluster.owner_avatar_url} />
                    <span className="text-[13px] text-foreground/80">{cluster.owner_name}</span>
                  </div>
                </UserResourcesDialog>
              ) : (
                <span className="text-[13px] italic text-muted-foreground/50">free</span>
              )}
            </div>
          )}

          {/* Chevron */}
          <svg
            aria-hidden="true"
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Action bar — always visible below header */}
      <div className="flex items-center gap-2 border-t border-border/50 px-3 py-2 sm:px-5">
        <NodeGroupPills cluster={cluster} />
        <div className="ml-auto flex items-center gap-1.5">
          {isProduction ? (
            isAdmin && (
              <Button
                size="sm"
                variant="outline"
                disabled={unmarkProduction.isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  setDialog("unmark");
                }}
                className="h-7 text-[12px]"
              >
                {unmarkProduction.isPending ? "Unmarking..." : "Unmark production"}
              </Button>
            )
          ) : (
            <>
              {isFree && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={acquireCluster.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    acquireCluster.mutate(cluster.id);
                  }}
                  className="h-7 border-emerald-300 bg-emerald-50 text-[12px] text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20"
                >
                  {acquireCluster.isPending ? "Acquiring..." : "Acquire"}
                </Button>
              )}
              {canRelease && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={releaseCluster.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    releaseCluster.mutate(cluster.id);
                  }}
                  className="h-7 text-[12px] text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                >
                  {releaseCluster.isPending ? "Releasing..." : "Release"}
                </Button>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={markProduction.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDialog("mark");
                  }}
                  className="h-7 text-[12px] text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
                >
                  Mark as production
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded content — machines */}
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 sm:px-5 sm:pb-4">
          <ClusterMachinesSection clusterId={cluster.id} />
        </div>
      )}

      <ConfirmDialog
        open={dialog === "mark"}
        onOpenChange={(next) => !next && setDialog(null)}
        title={`Mark ${cluster.name} as production?`}
        description="Removes the cluster from the pool — no acquire/release, and its member GPU node VMs are dropped from Fleet (the cloud instances and the cluster keep running). Also sets the Nebius production=true label. Reversible via Unmark production."
        confirmLabel="Mark production"
        variant="destructive"
        disabled={markProduction.isPending}
        onConfirm={() => markProduction.mutate(cluster.id)}
      />
      <ConfirmDialog
        open={dialog === "unmark"}
        onOpenChange={(next) => !next && setDialog(null)}
        title={`Return ${cluster.name} to the pool?`}
        description="Clears the production marking and removes the Nebius production=true label. Member nodes re-enter the pool as unowned on the next inventory sync."
        confirmLabel="Unmark production"
        onConfirm={() => unmarkProduction.mutate(cluster.id)}
      />
    </div>
  );
}

function ProductionClustersSection({ clusters }: { clusters: K8sCluster[] }) {
  const [open, setOpen] = useState(false);
  if (clusters.length === 0) return null;

  return (
    <div className="mt-6">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <svg
          aria-hidden="true"
          className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        Production clusters ({clusters.length})
        <span className="text-[11px] font-normal text-muted-foreground/60">
          — excluded from the pool
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-4">
          {clusters.map((cluster) => (
            <ClusterCard key={cluster.id} cluster={cluster} />
          ))}
        </div>
      )}
    </div>
  );
}

export function K8sClustersPage() {
  usePageTitle("K8s Clusters");
  const { data: clusters = [], isLoading } = useK8sClusters();

  const poolClusters = clusters.filter((cluster) => !cluster.production);
  const productionClusters = clusters.filter((cluster) => cluster.production);

  return (
    <div className="px-4 py-4 sm:px-6 sm:py-5">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-[15px] font-semibold text-foreground">K8s Clusters</h1>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">Loading K8s clusters...</span>
        </div>
      ) : clusters.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">
            No K8s clusters found. Clusters are auto-discovered from Nebius during inventory sync.
          </span>
        </div>
      ) : (
        <>
          {poolClusters.length > 0 ? (
            <div className="space-y-4">
              {poolClusters.map((cluster) => (
                <ClusterCard key={cluster.id} cluster={cluster} />
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-[13px] text-muted-foreground">
              No pool clusters — all discovered clusters are marked production.
            </div>
          )}
          <ProductionClustersSection clusters={productionClusters} />
        </>
      )}
    </div>
  );
}
